/**
 * Pricing admin page.
 *
 * Layout:
 *   PageHeader → 3 side-by-side plan cards (Starter / Business / Enterprise),
 *               each with a current/proposed split and slider-driven editors.
 *               → Apply preview strip showing MRR delta vs current pricing,
 *                 derived from license counts on each plan tier.
 *
 * Note: the API only exposes total base/overage cents — there's no
 * historical MRR endpoint, so the preview computes a snapshot:
 *   current MRR ≈ Σ(base_price_cents × active_licenses_on_tier)
 *   proposed MRR ≈ same, with the staged price.
 */
import { useEffect, useMemo, useState, type JSX } from 'react';
import {
  DollarSign, RefreshCw, RotateCcw, Save, Sparkles, Check,
} from 'lucide-react';
import { AdminApiClient,  type PlanPricingRow, type LicenseRow } from '../api';
import { userMessageFor, failureFrom, type Failure } from '../lib/api-state';
import { usePermissions } from '../lib/permissions';
import { AccessNotice } from '../components/ui/AccessNotice';
import { useSession } from '../session';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { StatusChip } from '../components/ui/StatusChip';
import { FetchState } from '../components/ui/FetchState';

type Tier = 'starter' | 'business' | 'enterprise';

const TIER_TONES: Record<Tier, string> = {
  starter:    '#94a3b8',
  business:   '#22d3ee',
  enterprise: '#c084fc',
};

const TIER_TAGLINES: Record<Tier, string> = {
  starter:    'Solo developers + side projects',
  business:   'Small teams, growing fleets',
  enterprise: 'Large fleets, custom needs',
};

interface Draft {
  base_price_cents: number;
  overage_price_cents: {
    max_accounts?: number;
    max_projects?: number;
    max_applications?: number;
  };
}

export function PricingPage(): JSX.Element {
  const { state } = useSession();
  const api = useMemo(
    () => new AdminApiClient({ baseUrl: '', getToken: () => state.token }),
    [state.token],
  );
  const perms = usePermissions();
  const canEdit = perms.canWrite;

  const [rows, setRows] = useState<PlanPricingRow[] | null>(null);
  const [licenses, setLicenses] = useState<LicenseRow[]>([]);
  // False when the license list failed: revenue estimates need the active
  // license counts, so without them they are "unknown", not $0.
  const [licensesOk, setLicensesOk] = useState(true);
  const [drafts, setDrafts] = useState<Record<Tier, Draft | null>>({
    starter: null, business: null, enterprise: null,
  });
  const [err, setErr] = useState<Failure | null>(null);
  const [version, setVersion] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    setRefreshing(true);
    Promise.all([
      api.listPricing().then((r) => r.plan_pricing)
        .catch((e) => { setErr(failureFrom(e, 'plan pricing')); return null; }),
      api.licenses({ limit: 1000 }).then((r) => r.licenses)
        .catch((e) => { failureFrom(e, 'the license counts'); return null; }),
    ]).then(([rs, ls]) => {
      setLicensesOk(ls !== null);
      setLicenses(ls ?? []);
      if (!rs) { setRows(null); return; }
      setErr(null);
      setRows(rs);
      const next: Record<Tier, Draft | null> = { starter: null, business: null, enterprise: null };
      for (const r of rs) {
        next[r.plan_tier] = {
          base_price_cents: r.base_price_cents,
          overage_price_cents: { ...r.overage_price_cents },
        };
      }
      setDrafts(next);
    }).finally(() => setRefreshing(false));
  }, [api, version]);

  const onRefresh = (): void => setVersion((v) => v + 1);

  const licenseCounts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const l of licenses) {
      if (l.status !== 'active') continue;
      c[l.plan_tier] = (c[l.plan_tier] ?? 0) + 1;
    }
    return c;
  }, [licenses]);

  const totals = useMemo(() => {
    let currentMRR = 0, proposedMRR = 0;
    for (const r of rows ?? []) {
      const count = licenseCounts[r.plan_tier] ?? 0;
      currentMRR += r.base_price_cents * count;
      const draft = drafts[r.plan_tier];
      if (draft) proposedMRR += draft.base_price_cents * count;
    }
    return {
      currentMRR,
      proposedMRR,
      delta: proposedMRR - currentMRR,
    };
  }, [rows, drafts, licenseCounts]);

  const dirty = useMemo(() => {
    if (!rows) return false;
    for (const r of rows) {
      const d = drafts[r.plan_tier];
      if (!d) continue;
      if (d.base_price_cents !== r.base_price_cents) return true;
      const a1 = d.overage_price_cents, a2 = r.overage_price_cents;
      if ((a1.max_accounts ?? 0) !== (a2.max_accounts ?? 0)) return true;
      if ((a1.max_projects ?? 0) !== (a2.max_projects ?? 0)) return true;
      if ((a1.max_applications ?? 0) !== (a2.max_applications ?? 0)) return true;
    }
    return false;
  }, [rows, drafts]);

  const updateDraft = (tier: Tier, patch: Partial<Draft>): void => {
    setDrafts((prev) => ({
      ...prev,
      [tier]: prev[tier] ? { ...prev[tier]!, ...patch, overage_price_cents: { ...prev[tier]!.overage_price_cents, ...(patch.overage_price_cents ?? {}) } } : null,
    }));
  };

  const updateOverage = (tier: Tier, key: 'max_accounts' | 'max_projects' | 'max_applications', value: number): void => {
    setDrafts((prev) => {
      if (!prev[tier]) return prev;
      return {
        ...prev,
        [tier]: {
          ...prev[tier]!,
          overage_price_cents: { ...prev[tier]!.overage_price_cents, [key]: value },
        },
      };
    });
  };

  const resetDraft = (tier: Tier): void => {
    const row = (rows ?? []).find((r) => r.plan_tier === tier);
    if (!row) return;
    updateDraft(tier, {
      base_price_cents: row.base_price_cents,
      overage_price_cents: { ...row.overage_price_cents },
    });
  };

  const saveTier = async (tier: Tier): Promise<void> => {
    const draft = drafts[tier];
    if (!draft) return;
    try {
      await api.updatePricing(tier, {
        base_price_cents: draft.base_price_cents,
        overage_price_cents: draft.overage_price_cents,
      });
      onRefresh();
    } catch (e) {
      alert(userMessageFor(e));
    }
  };

  const saveAll = async (): Promise<void> => {
    if (!confirm(licensesOk
      ? `Apply pricing changes? Estimated MRR change: ${fmtMoneyDelta(totals.delta)}.`
      : 'Apply pricing changes?')) return;
    for (const tier of ['starter', 'business', 'enterprise'] as Tier[]) {
      const d = drafts[tier];
      const r = (rows ?? []).find((x) => x.plan_tier === tier);
      if (!d || !r) continue;
      const tierDirty = d.base_price_cents !== r.base_price_cents
        || (d.overage_price_cents.max_accounts ?? 0) !== (r.overage_price_cents.max_accounts ?? 0)
        || (d.overage_price_cents.max_projects ?? 0) !== (r.overage_price_cents.max_projects ?? 0)
        || (d.overage_price_cents.max_applications ?? 0) !== (r.overage_price_cents.max_applications ?? 0);
      if (!tierDirty) continue;
      try {
        await api.updatePricing(tier, {
          base_price_cents: d.base_price_cents,
          overage_price_cents: d.overage_price_cents,
        });
      } catch (e) {
        alert(`Failed on ${tier}: ${userMessageFor(e)}`);
        return;
      }
    }
    onRefresh();
  };

  return (
    <div className="page">
      <PageHeader
        title="Plans"
        subtitle="Edit plan pricing side-by-side. Apply previews license-weighted MRR delta. Locked-model defaults below."
        icon={DollarSign}
        accent="#22d3ee"
        actions={
          <>
            <Button variant="outline" size="sm" onClick={onRefresh}>
              <RefreshCw width={14} height={14}
                style={{ animation: refreshing ? 'spin 900ms linear infinite' : undefined }} />
              Refresh
            </Button>
            {canEdit && (
              <Button
                variant="default"
                size="sm"
                onClick={() => void saveAll()}
                disabled={!dirty}
              >
                <Save width={14} height={14} />
                Apply changes
              </Button>
            )}
          </>
        }
      />

      <AccessNotice reason={canEdit ? null : `${perms.writeReason ?? ''} Editing plan pricing is not available to you.`} />
      {rows && !licensesOk && (
        <p role="alert" className="err" style={{ margin: '8px 0 16px', fontSize: '0.8125rem' }}>
          License counts could not be loaded, so revenue estimates are not shown.{' '}
          <button type="button" onClick={onRefresh}>Retry</button>
        </p>
      )}

      {/* Locked-model defaults table — sourced from
          @vloud-commercial/shared/constants TIER_DEFAULTS, which is
          the single source of truth (approved 2026-04-29 in
          vloud-license-server-v2-decisions.md §1). When a license is
          issued at a given tier, these values seed `limits_json`
          unless staff override at issuance. */}
      <section style={{
        margin: '8px 0 16px', padding: 12, borderRadius: 6,
        border: '1px solid var(--border)', background: 'var(--surface, transparent)',
      }}>
        <h2 style={{
          margin: '0 0 8px', fontSize: '0.75rem', textTransform: 'uppercase',
          letterSpacing: 0.5, color: 'var(--text-muted)',
        }}>
          Locked-model capacity defaults
        </h2>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
              <th style={{ padding: 6 }}>Tier</th>
              <th style={{ padding: 6 }}>Max instances</th>
              <th style={{ padding: 6 }}>Max hosting accounts</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderBottom: '1px solid var(--border-subtle, var(--border))' }}>
              <td style={{ padding: 6 }}>Starter</td>
              <td style={{ padding: 6, fontVariantNumeric: 'tabular-nums' }}>10</td>
              <td style={{ padding: 6, fontVariantNumeric: 'tabular-nums' }}>5</td>
            </tr>
            <tr style={{ borderBottom: '1px solid var(--border-subtle, var(--border))' }}>
              <td style={{ padding: 6 }}>Business</td>
              <td style={{ padding: 6, fontVariantNumeric: 'tabular-nums' }}>50</td>
              <td style={{ padding: 6, fontVariantNumeric: 'tabular-nums' }}>25</td>
            </tr>
            <tr>
              <td style={{ padding: 6 }}>Enterprise</td>
              <td style={{ padding: 6, fontVariantNumeric: 'tabular-nums' }}>unlimited</td>
              <td style={{ padding: 6, fontVariantNumeric: 'tabular-nums' }}>unlimited</td>
            </tr>
          </tbody>
        </table>
        <p style={{
          margin: '8px 0 0', fontSize: '0.75rem', color: 'var(--text-muted)',
        }}>
          Per-license overrides happen at issuance (Licenses → Issue) or via the
          LicenseDetail page. Custom + offline tiers carry per-contract limits.
        </p>
      </section>

      <FetchState
        loading={!rows && !err}
        error={err}
        empty={!!rows && rows.length === 0}
        emptyTitle="No pricing rows"
        onRetry={onRefresh}
      >
        <div
          style={{
            display: 'grid',
            gap: 12,
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            marginBottom: 16,
          }}
        >
          {(['starter', 'business', 'enterprise'] as Tier[]).map((tier) => {
            const row = (rows ?? []).find((r) => r.plan_tier === tier);
            const draft = drafts[tier];
            if (!row || !draft) return null;
            return (
              <PlanCard
                key={tier}
                tier={tier}
                row={row}
                draft={draft}
                licenseCount={licensesOk ? (licenseCounts[tier] ?? 0) : null}
                canEdit={!!canEdit}
                onChange={(patch) => updateDraft(tier, patch)}
                onOverage={(k, v) => updateOverage(tier, k, v)}
                onReset={() => resetDraft(tier)}
                onSave={() => void saveTier(tier)}
              />
            );
          })}
        </div>

        {licensesOk && <ApplyPreview
          rows={rows ?? []}
          drafts={drafts}
          counts={licenseCounts}
          totals={totals}
          dirty={dirty}
        />}
      </FetchState>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Plan card with side-by-side current vs proposed
 * ────────────────────────────────────────────────────────────────── */

function PlanCard({
  tier, row, draft, licenseCount, canEdit, onChange, onOverage, onReset, onSave,
}: {
  tier: Tier;
  row: PlanPricingRow;
  draft: Draft;
  licenseCount: number | null;
  canEdit: boolean;
  onChange: (patch: Partial<Draft>) => void;
  onOverage: (key: 'max_accounts' | 'max_projects' | 'max_applications', value: number) => void;
  onReset: () => void;
  onSave: () => void;
}): JSX.Element {
  const tone = TIER_TONES[tier];
  const tierDirty = draft.base_price_cents !== row.base_price_cents
    || (draft.overage_price_cents.max_accounts ?? 0) !== (row.overage_price_cents.max_accounts ?? 0)
    || (draft.overage_price_cents.max_projects ?? 0) !== (row.overage_price_cents.max_projects ?? 0)
    || (draft.overage_price_cents.max_applications ?? 0) !== (row.overage_price_cents.max_applications ?? 0);

  const currentMRR = row.base_price_cents * (licenseCount ?? 0);
  const proposedMRR = draft.base_price_cents * (licenseCount ?? 0);

  return (
    <article
      style={{
        position: 'relative',
        padding: 16,
        borderRadius: 14,
        border: `1px solid ${tierDirty ? tone : 'var(--border)'}`,
        background: 'var(--bg-raised)',
        display: 'flex', flexDirection: 'column', gap: 12,
      }}
    >
      <header style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div>
          <h3
            style={{
              margin: 0, fontSize: '1rem',
              textTransform: 'capitalize',
              color: tone,
            }}
          >
            {tier}
          </h3>
          <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            {TIER_TAGLINES[tier]}
          </p>
        </div>
        {tierDirty && (
          <StatusChip band="degraded" size="sm" noDot>modified</StatusChip>
        )}
      </header>

      <div
        style={{
          display: 'grid', gap: 10, gridTemplateColumns: '1fr 1fr',
          padding: 10, borderRadius: 10,
          background: 'var(--bg-overlay)',
        }}
      >
        <div>
          <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
            Current
          </div>
          <div style={{ fontSize: '1.25rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            {fmtMoney(row.base_price_cents)}
          </div>
          <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>per month</div>
        </div>
        <div>
          <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
            Proposed
          </div>
          <div style={{ fontSize: '1.25rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: tierDirty ? tone : undefined }}>
            {fmtMoney(draft.base_price_cents)}
          </div>
          <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>per month</div>
        </div>
      </div>

      {canEdit && (
        <SliderRow
          label="Base price"
          value={draft.base_price_cents}
          step={500}
          min={0}
          max={50000}
          tone={tone}
          onChange={(v) => onChange({ base_price_cents: v })}
        />
      )}

      <div
        style={{
          display: 'flex', flexDirection: 'column', gap: 8,
          paddingTop: 8, borderTop: '1px solid var(--border)',
        }}
      >
        <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Overage (¢ per extra unit)
        </div>
        {(['max_accounts', 'max_projects', 'max_applications'] as const).map((k) => (
          <SliderRow
            key={k}
            label={k.replace('max_', '').replace(/^./, (c) => c.toUpperCase())}
            value={draft.overage_price_cents[k] ?? 0}
            step={1}
            min={0}
            max={500}
            tone={tone}
            unit="¢"
            disabled={!canEdit}
            onChange={(v) => onOverage(k, v)}
          />
        ))}
      </div>

      <div
        style={{
          display: 'flex', flexDirection: 'column', gap: 4,
          paddingTop: 8,
          borderTop: '1px solid var(--border)',
          fontSize: '0.75rem',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ color: 'var(--text-muted)' }}>Active licenses</span>
          <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>
            {licenseCount === null ? '—' : licenseCount.toLocaleString()}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ color: 'var(--text-muted)' }} title="List price × active licenses on this plan">Est. current MRR</span>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{licenseCount === null ? '—' : fmtMoney(currentMRR)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ color: 'var(--text-muted)' }}>Est. proposed MRR</span>
          <span style={{ fontVariantNumeric: 'tabular-nums', color: tierDirty ? tone : undefined }}>
            {licenseCount === null ? '—' : fmtMoney(proposedMRR)}
          </span>
        </div>
        {tierDirty && licenseCount !== null && (
          <div
            style={{
              display: 'flex', justifyContent: 'space-between',
              fontWeight: 600,
              color: proposedMRR >= currentMRR ? 'var(--success)' : 'var(--danger)',
            }}
          >
            <span>Δ</span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              {fmtMoneyDelta(proposedMRR - currentMRR)}
            </span>
          </div>
        )}
      </div>

      {canEdit && tierDirty && (
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="ghost" size="sm" onClick={onReset} style={{ flex: 1 }}>
            <RotateCcw width={14} height={14} />
            Reset
          </Button>
          <Button variant="default" size="sm" onClick={onSave} style={{ flex: 1 }}>
            <Save width={14} height={14} />
            Save
          </Button>
        </div>
      )}

      <div
        style={{
          fontSize: '0.6875rem',
          color: 'var(--text-muted)',
          textAlign: 'right',
        }}
      >
        Last updated {new Date(row.updated_at).toISOString().slice(0, 10)}
      </div>
    </article>
  );
}

function SliderRow({
  label, value, min, max, step, tone, onChange, unit = '¢', disabled,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  tone: string;
  onChange: (v: number) => void;
  unit?: string;
  disabled?: boolean;
}): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem' }}>
        <span style={{ color: 'var(--text-muted)' }}>{label}</span>
        <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 500, color: tone }}>
          {unit === '¢' ? `${value}¢` : fmtMoney(value)}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        disabled={disabled}
        style={{
          accentColor: tone,
        }}
      />
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Apply preview banner
 * ────────────────────────────────────────────────────────────────── */

function ApplyPreview({
  rows, drafts, counts, totals, dirty,
}: {
  rows: PlanPricingRow[];
  drafts: Record<Tier, Draft | null>;
  counts: Record<string, number>;
  totals: { currentMRR: number; proposedMRR: number; delta: number };
  dirty: boolean;
}): JSX.Element {
  return (
    <section
      style={{
        padding: 16,
        borderRadius: 14,
        background: dirty ? 'rgba(99, 102, 241, 0.08)' : 'var(--bg-raised)',
        border: `1px solid ${dirty ? 'var(--accent)' : 'var(--border)'}`,
        display: 'flex', flexDirection: 'column', gap: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {dirty ? (
          <Sparkles width={14} height={14} color="var(--accent)" />
        ) : (
          <Check width={14} height={14} color="var(--success)" />
        )}
        <h3
          style={{
            margin: 0,
            fontSize: '0.6875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: dirty ? 'var(--accent)' : 'var(--success)',
            fontWeight: 600,
          }}
        >
          {dirty ? 'Apply preview' : 'No pending changes'}
        </h3>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        {(['starter', 'business', 'enterprise'] as Tier[]).map((tier) => {
          const row = rows.find((r) => r.plan_tier === tier);
          const draft = drafts[tier];
          const count = counts[tier] ?? 0;
          if (!row || !draft) return null;
          const cur = row.base_price_cents * count;
          const next = draft.base_price_cents * count;
          const delta = next - cur;
          const tierDirty = delta !== 0
            || (draft.overage_price_cents.max_accounts ?? 0) !== (row.overage_price_cents.max_accounts ?? 0)
            || (draft.overage_price_cents.max_projects ?? 0) !== (row.overage_price_cents.max_projects ?? 0)
            || (draft.overage_price_cents.max_applications ?? 0) !== (row.overage_price_cents.max_applications ?? 0);
          return (
            <div
              key={tier}
              style={{
                padding: 10,
                borderRadius: 10,
                background: 'var(--bg-overlay)',
                border: '1px solid var(--border)',
              }}
            >
              <div
                style={{
                  fontSize: '0.6875rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  color: TIER_TONES[tier],
                  fontWeight: 600,
                  marginBottom: 6,
                }}
              >
                {tier}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                <span>licenses</span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{count}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem' }}>
                <span style={{ color: 'var(--text-muted)' }}>MRR</span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {fmtMoney(cur)} → {fmtMoney(next)}
                </span>
              </div>
              {tierDirty && (
                <div
                  style={{
                    display: 'flex', justifyContent: 'space-between',
                    fontSize: '0.75rem',
                    color: delta >= 0 ? 'var(--success)' : 'var(--danger)',
                    fontWeight: 600,
                    marginTop: 2,
                  }}
                >
                  <span>Δ</span>
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoneyDelta(delta)}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          paddingTop: 8, borderTop: '1px solid var(--border)',
          fontSize: '0.875rem',
        }}
      >
        <span style={{ color: 'var(--text-muted)' }} title="List price × active licenses; not invoiced revenue">Est. fleet MRR</span>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontVariantNumeric: 'tabular-nums' }}>
          <span>{fmtMoney(totals.currentMRR)}</span>
          {dirty && (
            <>
              <span style={{ color: 'var(--text-muted)' }}>→</span>
              <span style={{ fontWeight: 600 }}>{fmtMoney(totals.proposedMRR)}</span>
              <span
                style={{
                  fontWeight: 600,
                  color: totals.delta >= 0 ? 'var(--success)' : 'var(--danger)',
                }}
              >
                {fmtMoneyDelta(totals.delta)}
              </span>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Helpers
 * ────────────────────────────────────────────────────────────────── */

function fmtMoney(cents: number): string {
  if (Math.abs(cents) >= 100_000_00) {
    return `$${(cents / 100_000).toFixed(0)}k`;
  }
  return `$${(cents / 100).toFixed(2)}`;
}

function fmtMoneyDelta(cents: number): string {
  const sign = cents >= 0 ? '+' : '−';
  return `${sign}${fmtMoney(Math.abs(cents))}`;
}
